import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Layout from './Layout';
import { hasPermission } from '../utils/permissions';
import { companyHasFeature } from '../utils/planFeatures';

// ---------------------------------------------------------------------------
// Route-level lazy imports — each page lands in its own chunk.
// All pages use default exports, so the bare lazy(() => import(...)) form works.
// ---------------------------------------------------------------------------

// Admin Portal Pages
const Dashboard = lazy(() => import('../pages/Dashboard'));
const UserManual = lazy(() => import('../pages/UserManual'));
const JobCards = lazy(() => import('../pages/JobCards'));
const JobCardDetails = lazy(() => import('../pages/JobCardDetails'));
const InspectionCapture = lazy(() => import('../pages/inspections/InspectionCapture'));
const Inspections = lazy(() => import('../pages/inspections/Inspections'));
const InspectionTemplates = lazy(() => import('../pages/inspections/InspectionTemplates'));
const Parts = lazy(() => import('../pages/Parts'));
const InventoryLocations = lazy(() => import('../pages/InventoryLocations'));
const BranchTransfers = lazy(() => import('../pages/BranchTransfers'));
const BranchTransferForm = lazy(() => import('../pages/BranchTransferForm'));
const BranchTransferDetail = lazy(() => import('../pages/BranchTransferDetail'));
const Categories = lazy(() => import('../pages/Categories'));
const Customers = lazy(() => import('../pages/Customers'));
const CustomerProfile = lazy(() => import('../pages/CustomerProfile'));
const CustomerDuplicates = lazy(() => import('../pages/CustomerDuplicates'));
const Vehicles = lazy(() => import('../pages/Vehicles'));
const VehicleProfile = lazy(() => import('../pages/VehicleProfile'));
const Users = lazy(() => import('../pages/Users'));
const Invoices = lazy(() => import('../pages/Invoices'));
const InvoiceDetails = lazy(() => import('../pages/InvoiceDetails'));
const CreateInvoice = lazy(() => import('../pages/CreateInvoice'));
const Services = lazy(() => import('../pages/Services'));
const Quotations = lazy(() => import('../pages/Quotations'));
const CreateQuotation = lazy(() => import('../pages/CreateQuotation'));
const QuotationDetails = lazy(() => import('../pages/QuotationDetails'));
const Reports = lazy(() => import('../pages/Reports'));
const Requests = lazy(() => import('../pages/Requests'));
const Approvals = lazy(() => import('../pages/Approvals'));
const CustomerFeedbackList = lazy(() => import('../pages/CustomerFeedbackList'));
const TaskMonitoring = lazy(() => import('../pages/TaskMonitoring'));
const PermissionManager = lazy(() => import('../pages/PermissionManager'));
const CashStatement = lazy(() => import('../pages/financials/CashStatement'));
const ChartOfAccounts = lazy(() => import('../pages/financials/ChartOfAccounts'));
const AccountMappings = lazy(() => import('../pages/financials/AccountMappings'));
const OpeningBalances = lazy(() => import('../pages/financials/OpeningBalances'));
const TrialBalance = lazy(() => import('../pages/financials/TrialBalance'));
// The three IFRS statements (Batch B). Same lazy shape, same Financials group,
// same journal_entries.view gate as the trial balance beside them — the
// endpoints stack requireFeature('general_ledger') on top of that pair, so a
// tenant without the flag gets an empty screen rather than a 404.
const IncomeStatement = lazy(() => import('../pages/financials/IncomeStatement'));
const CashFlow = lazy(() => import('../pages/financials/CashFlow'));
const ChangesInEquity = lazy(() => import('../pages/financials/ChangesInEquity'));
const AccountingSettings = lazy(() => import('../pages/financials/AccountingSettings'));
const JournalEntries = lazy(() => import('../pages/financials/JournalEntries'));
const PeriodClose = lazy(() => import('../pages/financials/PeriodClose'));
const FixedAssets = lazy(() => import('../pages/financials/FixedAssets'));
const WarrantyMonitor = lazy(() => import('../pages/warranties/WarrantyMonitor'));
const Expenses = lazy(() => import('../pages/financials/Expenses'));
const FinancialAudit = lazy(() => import('../pages/financials/FinancialAudit'));
const Partners = lazy(() => import('../pages/financials/Partners'));
const PartnerStatement = lazy(() => import('../pages/financials/PartnerStatement'));
const FinancialPosition = lazy(() => import('../pages/financials/FinancialPosition'));
const BankReconciliation = lazy(() => import('../pages/financials/BankReconciliation'));
const BankStatementReview = lazy(() => import('../pages/financials/BankStatementReview'));
const BankStatementImport = lazy(() => import('../pages/financials/BankStatementImport'));
const Purchases = lazy(() => import('../pages/purchases/Purchases'));
const Suppliers = lazy(() => import('../pages/purchases/Suppliers'));
const SupplierProfile = lazy(() => import('../pages/purchases/SupplierProfile'));
const SupplierDuplicates = lazy(() => import('../pages/purchases/SupplierDuplicates'));
const Employees = lazy(() => import('../pages/hr/Employees'));
const EmployeeNew = lazy(() => import('../pages/hr/EmployeeNew'));
const EmployeeFile = lazy(() => import('../pages/hr/EmployeeFile'));
const PayrollRuns = lazy(() => import('../pages/hr/PayrollRuns'));
const EosbOpeningProvision = lazy(() => import('../pages/hr/EosbOpeningProvision'));
const PayrollRunDetail = lazy(() => import('../pages/hr/PayrollRunDetail'));
const HrReports = lazy(() => import('../pages/hr/HrReports'));
const HrSettings = lazy(() => import('../pages/hr/HrSettings'));
const EmployeeProfitability = lazy(() => import('../pages/hr/EmployeeProfitability'));
const WhatsAppConnect = lazy(() => import('../pages/WhatsAppConnect'));
const WhatsAppInbox = lazy(() => import('../pages/WhatsAppInbox'));
const WhatsAppMarketing = lazy(() => import('../pages/WhatsAppMarketing'));

// Settings hub + category sub-pages (split out of the old monolithic Settings).
const SettingsLayout = lazy(() => import('../pages/settings/SettingsLayout'));
const GeneralSettings = lazy(() => import('../pages/settings/GeneralSettings'));
const NotificationSettings = lazy(() => import('../pages/settings/NotificationSettings'));
const RevenueSettings = lazy(() => import('../pages/settings/RevenueSettings'));
const PaymentSettings = lazy(() => import('../pages/settings/PaymentSettings'));
const TermsSettings = lazy(() => import('../pages/settings/TermsSettings'));
const FeatureSettings = lazy(() => import('../pages/settings/FeatureSettings'));
const OnlineBookingLayout = lazy(() => import('../pages/settings/booking/OnlineBookingLayout'));
const WebsiteDesign = lazy(() => import('../pages/settings/WebsiteDesign'));
const CreditReminderSettings = lazy(() => import('../pages/settings/CreditReminderSettings'));
const FeedbackSettings = lazy(() => import('../pages/settings/FeedbackSettings'));
const ExpenseCategorySettings = lazy(() => import('../pages/settings/ExpenseCategorySettings'));
const WhatsAppSettings = lazy(() => import('../pages/settings/WhatsAppSettings'));
const DataExportSettings = lazy(() => import('../pages/settings/DataExportSettings'));
const DelayFeeSettings = lazy(() => import('../pages/settings/DelayFeeSettings'));
const CompanyCatalogue = lazy(() => import('../pages/settings/CompanyCatalogue'));
const TenantPayments = lazy(() => import('../pages/TenantPayments'));
const PaymentReceipts = lazy(() => import('../pages/PaymentReceipts'));
const InsuranceCompanies = lazy(() => import('../pages/InsuranceCompanies'));

// Fleet F1 — list, 11-step setup wizard (new + edit), details, SLA templates.
const Fleets = lazy(() => import('../pages/Fleets'));
const FleetSetupWizard = lazy(() => import('../pages/FleetSetupWizard'));
const FleetDetails = lazy(() => import('../pages/FleetDetails'));
const FleetSlaTemplates = lazy(() => import('../pages/FleetSlaTemplates'));
// Fleet F5 (plan Task 7): the maintenance due list — a fleet.read page.
const FleetDueList = lazy(() => import('../components/fleet/FleetDueList'));

// Super Admin Pages
const SuperAdminDashboard = lazy(() => import('../pages/superadmin/SuperAdminDashboard'));
const ManualInsights = lazy(() => import('../pages/superadmin/ManualInsights'));
const Companies = lazy(() => import('../pages/superadmin/Companies'));
const CompanyDetails = lazy(() => import('../pages/superadmin/CompanyDetails'));
const Subscriptions = lazy(() => import('../pages/superadmin/Subscriptions'));
const SuperAdminUsers = lazy(() => import('../pages/superadmin/SuperAdminUsers'));
const Configurations = lazy(() => import('../pages/superadmin/Configurations'));
const Garages = lazy(() => import('../pages/superadmin/Garages'));
const TenantUsers = lazy(() => import('../pages/superadmin/TenantUsers'));
const PaymentSuppliers = lazy(() => import('../pages/superadmin/PaymentSuppliers'));
const ManagedPayments = lazy(() => import('../pages/superadmin/ManagedPayments'));
const Payouts = lazy(() => import('../pages/superadmin/Payouts'));
const Onboarding = lazy(() => import('../pages/superadmin/Onboarding'));
const PlatformAnalytics = lazy(() => import('../pages/superadmin/PlatformAnalytics'));
const HaStatus = lazy(() => import('../pages/superadmin/HaStatus'));
const NotificationChannels = lazy(() => import('../pages/superadmin/NotificationChannels'));
const AdminNotifications = lazy(() => import('../pages/superadmin/AdminNotifications'));
const SuperAdminAnnouncements = lazy(() => import('../pages/superadmin/Announcements'));
const SuperAdminPartCategories = lazy(() => import('../pages/superadmin/PartCategories'));
const VehicleCatalog = lazy(() => import('../pages/superadmin/VehicleCatalog'));

const Appointments = lazy(() => import('../pages/Appointments'));
const Assistant = lazy(() => import('../pages/Assistant'));

// Mechanic Pages
const MyJobs = lazy(() => import('../pages/mechanic/MyJobs'));
const MechanicJobDetails = lazy(() => import('../pages/mechanic/JobDetailsNew'));
const TaskDetail = lazy(() => import('../pages/mechanic/TaskDetail'));

// ---------------------------------------------------------------------------
// Minimal Suspense fallback — same spinner tokens as App.js auth screen.
// Rendered inside Layout so the shell (sidebar/header) stays visible while
// individual page chunks load.
// ---------------------------------------------------------------------------
function PageFallback() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-surface-3 border-t-accent" />
    </div>
  );
}

// Nested Settings routes — the hub layout renders a sub-rail + <Outlet/>.
// Returns fresh elements each call so it can be embedded in multiple <Routes>.
const renderSettingsRoutes = () => (
  <Route path="/settings" element={<SettingsLayout />}>
    <Route path="general" element={<GeneralSettings />} />
    <Route path="notifications" element={<NotificationSettings />} />
    <Route path="revenue" element={<RevenueSettings />} />
    <Route path="payments" element={<PaymentSettings />} />
    <Route path="terms" element={<TermsSettings />} />
    <Route path="features" element={<FeatureSettings />} />
    {/* Appointments v2: the old page moved into the Online booking tabs. */}
    <Route
      path="appointments"
      element={<Navigate to="/settings/online-booking/general" replace />}
    />
    <Route path="online-booking/*" element={<OnlineBookingLayout />} />
    <Route path="website-design" element={<WebsiteDesign />} />
    <Route path="credit-reminders" element={<CreditReminderSettings />} />
    <Route path="feedback" element={<FeedbackSettings />} />
    <Route path="expense-categories" element={<ExpenseCategorySettings />} />
    <Route path="whatsapp" element={<WhatsAppSettings />} />
    <Route path="delay-fees" element={<DelayFeeSettings />} />
    <Route path="data-export" element={<DataExportSettings />} />
    <Route path="company-catalogue" element={<CompanyCatalogue />} />
  </Route>
);

const RoleBasedRouter = () => {
  const { user, viewMode } = useSelector((state) => state.auth);

  if (!user || !user.role) {
    return <Navigate to="/login" />;
  }

  const roleName = user.role.name;
  const canManageInsuranceCompanies = hasPermission(user, 'insurance_companies', 'manage');
  // Fleet F1: the plan feature AND the permission, the same pair the fleet
  // endpoints stack (requireFeature('fleet_management') + fleet.read/manage).
  // Static paths (/fleets/new, /fleets/sla-templates) are declared before
  // /fleets/:id so they can never be eaten as an id.
  const fleetOn = companyHasFeature(user?.company, 'fleet_management');
  const canReadFleets = fleetOn && hasPermission(user, 'fleet', 'read');
  const canManageFleets = fleetOn && hasPermission(user, 'fleet', 'manage');
  const canManageUsers = hasPermission(user, 'users', 'read');
  const canManagePermissions = hasPermission(user, 'permissions', 'manage');
  const canViewCash = hasPermission(user, 'cash', 'view');
  const canViewBankRecon = hasPermission(user, 'bank_recon', 'view');
  const canViewExpenses = hasPermission(user, 'expenses', 'view');
  const canViewPurchases = canViewExpenses || hasPermission(user, 'purchase_invoices', 'read');
  // Multi-branch Release 2 duplicates reports: the feature key AND the merge
  // key, so an Admin of a tenant with the key OFF sees exactly today's portal.
  const canMergeCustomers =
    Number(user?.company?.branch_count) > 1 &&
    companyHasFeature(user?.company, 'shared_customers') &&
    hasPermission(user, 'customers', 'merge');
  const canMergeSuppliers =
    Number(user?.company?.branch_count) > 1 &&
    companyHasFeature(user?.company, 'shared_suppliers') &&
    hasPermission(user, 'suppliers', 'merge');
  const canViewFinancialAudit = hasPermission(user, 'financials', 'audit_view');
  const canViewPartners = hasPermission(user, 'partners', 'view');
  // General ledger. gl_accounts guards the chart; journal_entries guards the
  // trial balance, the JV grid and period close — the same split the
  // endpoints use, so a grant here never opens more than the API allows.
  const canViewGlAccounts = hasPermission(user, 'gl_accounts', 'view');
  // Account mapping: its own resource, same read gate as GET /api/account-mappings.
  const canViewAccountMappings = hasPermission(user, 'account_mappings', 'view');
  const canViewOpeningBalances = hasPermission(user, 'opening_balances', 'view');
  const canViewFixedAssets = hasPermission(user, 'fixed_assets', 'view');
  const canViewWarranties = hasPermission(user, 'warranties', 'view');
  const canViewJournal = hasPermission(user, 'journal_entries', 'view');
  const canConnectWhatsapp = hasPermission(user, 'whatsapp', 'connect');
  const canChatWhatsapp = hasPermission(user, 'whatsapp', 'chat');
  const canMarketWhatsapp = hasPermission(user, 'whatsapp', 'marketing');
  // Feature-off is handled INSIDE Assistant.js itself (upsell-free state on a
  // direct hit, per the design spec) — this only gates the RBAC permission,
  // same pattern as every other permission-gated route below.
  const canUseSmartAssistant = hasPermission(user, 'smart_assistant', 'use');
  const canViewHrEmployees = hasPermission(user, 'hr', 'employees.read');
  const canManageHrEmployees = hasPermission(user, 'hr', 'employees.manage');
  const canViewHrPayroll = hasPermission(user, 'hr', 'payroll.read');
  const canViewHrReports = hasPermission(user, 'hr', 'reports.read');
  // Requests inbox (staff issue reports: task problems, service/part defects,
  // assistance pings) — same permission the PATCH resolve endpoint requires.
  const canManageRequests = hasPermission(user, 'issue_reports', 'manage');
  // Approvals inbox — the same any-of gate as GET /api/approvals.
  const canApprove =
    hasPermission(user, 'discounts', 'approve') ||
    hasPermission(user, 'purchase_invoices', 'approve');

  // Super Admin Routes
  if (roleName === 'Super Admin') {
    return (
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<SuperAdminDashboard />} />
            <Route path="/analytics" element={<PlatformAnalytics />} />
            <Route path="/ha-status" element={<HaStatus />} />
            <Route path="/notification-channels" element={<NotificationChannels />} />
            <Route path="/admin-notifications" element={<AdminNotifications />} />
            {/* The platform WhatsApp link moved into Settings ›
                            WhatsApp. Kept as a redirect: the owner has the old
                            URL in his history and a bare 404 is a worse answer
                            than the page he wanted. */}
            <Route path="/whatsapp-link" element={<Navigate to="/settings/whatsapp" replace />} />
            <Route path="/announcements" element={<SuperAdminAnnouncements />} />
            <Route path="/manual-insights" element={<ManualInsights />} />
            {/* Drill into a tenant's job cards from the analytics
                            page. Super Admin is backend-unrestricted; the active
                            garage is carried via the X-Garage-ID header set when
                            a branch row is clicked. */}
            <Route path="/job-cards" element={<JobCards />} />
            <Route path="/job-cards/:id" element={<JobCardDetails />} />
            <Route path="/task-monitoring" element={<TaskMonitoring />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:id" element={<CompanyDetails />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/users" element={<SuperAdminUsers />} />
            <Route path="/tenant-users" element={<TenantUsers />} />
            <Route path="/payment-suppliers" element={<PaymentSuppliers />} />
            <Route path="/managed-payments" element={<ManagedPayments />} />
            <Route path="/payouts" element={<Payouts />} />
            <Route path="/configurations" element={<Configurations />} />
            <Route path="/garages" element={<Garages />} />
            {/* Path matches the literal link vehicleCatalogMatcher.js's
                            super-admin bell notification stamps (ntf.catalogMiss) —
                            keep them in sync if either ever changes. */}
            <Route path="/super-admin/vehicle-catalog" element={<VehicleCatalog />} />
            {/* The global part catalogue. Tenants manage their own
                            categories at /categories; these is_global rows are
                            shared by every tenant and only the platform edits
                            them. */}
            <Route path="/part-categories" element={<SuperAdminPartCategories />} />
            {renderSettingsRoutes()}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </Layout>
    );
  }

  // Staff Routes (or Admin/Manager in Staff view mode)
  if (roleName === 'Staff' || viewMode === 'staff') {
    return (
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<MyJobs />} />
            <Route path="/jobs/:id" element={<MechanicJobDetails />} />
            <Route path="/jobs/:jobId/tasks/:taskId" element={<TaskDetail />} />
            {/* A فحص شامل checklist is filled at the bay, by the mechanic. */}
            <Route path="/inspections/:id" element={<InspectionCapture />} />
            {/* Staff read-only access to a vehicle's history profile */}
            <Route path="/vehicles/:id" element={<VehicleProfile />} />
            <Route path="/manual" element={<UserManual />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </Layout>
    );
  }

  // Receptionist role - limited access routes
  if (roleName === 'Receptionist') {
    return (
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/job-cards" element={<JobCards />} />
            <Route path="/job-cards/:id" element={<JobCardDetails />} />
            <Route path="/jobs/:id" element={<JobCardDetails />} />
            {/* The register first: '/inspections/:id' below would otherwise
                match it with id="inspections" and the page would never load. */}
            <Route path="/inspections" element={<Inspections />} />
            <Route path="/inspections/:id" element={<InspectionCapture />} />
            {/* List routes first; :id profile routes after so they don't shadow the lists */}
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerProfile />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/vehicles/:id" element={<VehicleProfile />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/create" element={<CreateInvoice />} />
            <Route path="/invoices/:id" element={<InvoiceDetails />} />
            <Route path="/quotations" element={<Quotations />} />
            <Route path="/quotations/create" element={<CreateQuotation />} />
            <Route path="/quotations/:id" element={<QuotationDetails />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/approvals" element={canApprove ? <Approvals /> : <Navigate to="/" />} />
            <Route path="/manual" element={<UserManual />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </Layout>
    );
  }

  // Admin, Manager, and other roles - Full Admin Portal
  // Subdomain handles tenant isolation, no need for URL paths
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route
            path="/assistant"
            element={canUseSmartAssistant ? <Assistant /> : <Navigate to="/" />}
          />
          <Route path="/job-cards" element={<JobCards />} />
          <Route path="/job-cards/:id" element={<JobCardDetails />} />
          <Route path="/jobs/:id" element={<JobCardDetails />} />
          <Route path="/inspections" element={<Inspections />} />
          <Route path="/inspections/:id" element={<InspectionCapture />} />
          {/* Its own top-level path, NOT '/inspections/templates': that would
              sit under ':id' above and the register's route-order trap would
              have to be re-solved in every role tree that ever gains it. */}
          <Route path="/inspection-templates" element={<InspectionTemplates />} />
          <Route path="/task-monitoring" element={<TaskMonitoring />} />
          <Route path="/appointments" element={<Appointments />} />
          <Route path="/parts" element={<Parts />} />
          <Route path="/inventory-locations" element={<InventoryLocations />} />
          {/* Not feature-gated: the pages gate themselves, and a transfer in
              transit stays receivable or cancellable after an untick. */}
          <Route path="/branch-transfers" element={<BranchTransfers />} />
          <Route path="/branch-transfers/new" element={<BranchTransferForm />} />
          <Route path="/branch-transfers/:id/edit" element={<BranchTransferForm />} />
          <Route path="/branch-transfers/:id" element={<BranchTransferDetail />} />
          <Route path="/categories" element={<Categories />} />
          {/* List routes first; :id profile routes after so they don't shadow the lists */}
          <Route path="/customers" element={<Customers />} />
          <Route
            path="/customers/duplicates"
            element={canMergeCustomers ? <CustomerDuplicates /> : <Navigate to="/" />}
          />
          <Route path="/customers/:id" element={<CustomerProfile />} />
          <Route path="/vehicles" element={<Vehicles />} />
          <Route path="/vehicles/:id" element={<VehicleProfile />} />
          <Route path="/users" element={canManageUsers ? <Users /> : <Navigate to="/" />} />
          {/* HR module (PR-1). List route first so it isn't shadowed by :id. */}
          <Route
            path="/hr/employees"
            element={canViewHrEmployees ? <Employees /> : <Navigate to="/" />}
          />
          <Route
            path="/hr/employees/new"
            element={canManageHrEmployees ? <EmployeeNew /> : <Navigate to="/" />}
          />
          <Route
            path="/hr/employees/:id"
            element={canViewHrEmployees ? <EmployeeFile /> : <Navigate to="/" />}
          />
          {/* HR payroll + reports (PR-2, Task 11). List route first so it isn't shadowed by :id. */}
          <Route
            path="/hr/payroll"
            element={canViewHrPayroll ? <PayrollRuns /> : <Navigate to="/" />}
          />
          {/* BEFORE '/hr/payroll/:id' — 'eosb-opening-provision' would
                        otherwise match as a run id and land on a 404 detail
                        screen. Read on payroll.read; the save and the catch-up
                        are gated inside the screen on payroll.approve, mirroring
                        routes/hr.js across the three endpoints. */}
          <Route
            path="/hr/payroll/eosb-opening-provision"
            element={canViewHrPayroll ? <EosbOpeningProvision /> : <Navigate to="/" />}
          />
          <Route
            path="/hr/payroll/:id"
            element={canViewHrPayroll ? <PayrollRunDetail /> : <Navigate to="/" />}
          />
          <Route
            path="/hr/reports"
            element={canViewHrReports ? <HrReports /> : <Navigate to="/" />}
          />
          {/* HR configuration (holidays, Ramadan, Nitaqat thresholds,
                        WPS, reminder recipients) — moved off the reports page
                        2026-07-26. payroll.manage OR employees.manage: the two
                        gates its cards carry. */}
          <Route
            path="/hr/settings"
            element={
              hasPermission(user, 'hr', 'payroll.manage') ||
              hasPermission(user, 'hr', 'employees.manage') ? (
                <HrSettings />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Employee profitability report (PR-5, Task 5) — same gate as the reports above. */}
          <Route
            path="/hr/reports/profitability"
            element={canViewHrReports ? <EmployeeProfitability /> : <Navigate to="/" />}
          />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/create" element={<CreateInvoice />} />
          <Route path="/invoices/:id" element={<InvoiceDetails />} />
          <Route path="/quotations" element={<Quotations />} />
          <Route path="/quotations/create" element={<CreateQuotation />} />
          <Route path="/quotations/:id" element={<QuotationDetails />} />
          {/* Legacy Purchase Invoices page retired (owner decision) — folded into Purchases hub. */}
          <Route path="/purchase-invoices" element={<Navigate replace to="/purchases" />} />
          <Route path="/payments" element={<TenantPayments />} />
          <Route path="/payment-receipts" element={<PaymentReceipts />} />
          <Route
            path="/insurance-companies"
            element={canManageInsuranceCompanies ? <InsuranceCompanies /> : <Navigate to="/" />}
          />
          {/* Fleet F1. Static paths BEFORE '/fleets/:id'. */}
          <Route path="/fleets" element={canReadFleets ? <Fleets /> : <Navigate to="/" />} />
          <Route
            path="/fleets/new"
            element={canManageFleets ? <FleetSetupWizard /> : <Navigate to="/" />}
          />
          <Route
            path="/fleets/sla-templates"
            element={canManageFleets ? <FleetSlaTemplates /> : <Navigate to="/" />}
          />
          {/* Fleet F5: the maintenance due list, fleet.read like the dashboard. */}
          <Route
            path="/fleets/due"
            element={canReadFleets ? <FleetDueList /> : <Navigate to="/" />}
          />
          <Route
            path="/fleets/:id/setup"
            element={canManageFleets ? <FleetSetupWizard /> : <Navigate to="/" />}
          />
          <Route
            path="/fleets/:id"
            element={canReadFleets ? <FleetDetails /> : <Navigate to="/" />}
          />
          <Route path="/services" element={<Services />} />
          <Route path="/reports" element={<Reports />} />
          <Route
            path="/requests"
            element={canManageRequests ? <Requests /> : <Navigate to="/" />}
          />
          <Route path="/approvals" element={canApprove ? <Approvals /> : <Navigate to="/" />} />
          <Route path="/feedback" element={<CustomerFeedbackList />} />
          <Route path="/manual" element={<UserManual />} />
          <Route
            path="/financials/cash"
            element={canViewCash ? <CashStatement /> : <Navigate to="/" />}
          />
          {/* Same cash:view gate the endpoint stacks on top of reports:read. */}
          <Route
            path="/financials/position"
            element={canViewCash ? <FinancialPosition /> : <Navigate to="/" />}
          />
          {/* bank_recon:view, same pair the endpoints check. The
                        bank_reconciliation FEATURE gate lives on the router
                        server-side, so a tenant without it gets an empty screen
                        rather than a 404 — the same shape as the other
                        default-off finance screens. */}
          <Route
            path="/financials/reconciliation"
            element={canViewBankRecon ? <BankReconciliation /> : <Navigate to="/" />}
          />
          {/* Upload a statement. Distinct segment from 'imports/:id'
                        below (singular vs plural), so order is not load-bearing. */}
          <Route
            path="/financials/reconciliation/import"
            element={canViewBankRecon ? <BankStatementImport /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/reconciliation/imports/:importId"
            element={canViewBankRecon ? <BankStatementReview /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/expenses"
            element={canViewExpenses ? <Expenses /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/audit"
            element={canViewFinancialAudit ? <FinancialAudit /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/partners"
            element={canViewPartners ? <Partners /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/partners/:id"
            element={canViewPartners ? <PartnerStatement /> : <Navigate to="/" />}
          />
          {/* General ledger. The `general_ledger` FEATURE gate lives
                        on the server router, same shape as bank reconciliation:
                        a tenant without the flag gets an empty screen, not a
                        404. Opening balances keeps its own resource — it is a
                        one-time setup wizard, not part of the ledger proper. */}
          <Route
            path="/financials/chart-of-accounts"
            element={canViewGlAccounts ? <ChartOfAccounts /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/account-mappings"
            element={canViewAccountMappings ? <AccountMappings /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/opening-balances"
            element={canViewOpeningBalances ? <OpeningBalances /> : <Navigate to="/" />}
          />
          {/* fixed_assets:view — the general_ledger FEATURE gate lives on the
                        server router, same shape as the other GL screens. */}
          <Route
            path="/financials/fixed-assets"
            element={canViewFixedAssets ? <FixedAssets /> : <Navigate to="/" />}
          />
          {/* warranties:view. The service_warranty FEATURE gate lives on the
              server router and inside the screen, same split as the GL pages:
              the route existing is not the same as the tenant having the
              feature, and a missing route redirects home with no explanation. */}
          <Route
            path="/warranties"
            element={canViewWarranties ? <WarrantyMonitor /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/trial-balance"
            element={canViewJournal ? <TrialBalance /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/journal-entries"
            element={canViewJournal ? <JournalEntries /> : <Navigate to="/" />}
          />
          {/* A menu entry whose route is absent from THIS role's tree
                        silently redirects home, so these three live in the same
                        block as the trial balance their menu entries sit
                        beside. Only the Admin/Manager tree holds any of them —
                        Staff, Receptionist and Super Admin have no Financials
                        group at all. */}
          <Route
            path="/financials/income-statement"
            element={canViewJournal ? <IncomeStatement /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/cash-flow"
            element={canViewJournal ? <CashFlow /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/changes-in-equity"
            element={canViewJournal ? <ChangesInEquity /> : <Navigate to="/" />}
          />
          {/* Read on journal_entries.view; the SAVE is gated inside the screen
                        on journal_entries.post, mirroring routes/gl.js's split
                        across GET and PUT /api/gl/settings. */}
          <Route
            path="/financials/accounting-settings"
            element={canViewJournal ? <AccountingSettings /> : <Navigate to="/" />}
          />
          <Route
            path="/financials/period-close"
            element={canViewJournal ? <PeriodClose /> : <Navigate to="/" />}
          />
          <Route
            path="/purchases"
            element={canViewPurchases ? <Purchases /> : <Navigate to="/" />}
          />
          <Route
            path="/purchases/suppliers"
            element={canViewPurchases ? <Suppliers /> : <Navigate to="/" />}
          />
          <Route
            path="/purchases/suppliers/duplicates"
            element={canMergeSuppliers ? <SupplierDuplicates /> : <Navigate to="/" />}
          />
          <Route
            path="/purchases/suppliers/:id"
            element={canViewPurchases ? <SupplierProfile /> : <Navigate to="/" />}
          />
          <Route
            path="/whatsapp"
            element={canChatWhatsapp ? <WhatsAppInbox /> : <Navigate to="/" />}
          />
          <Route
            path="/whatsapp/connect"
            element={canConnectWhatsapp ? <WhatsAppConnect /> : <Navigate to="/" />}
          />
          <Route
            path="/whatsapp/marketing"
            element={canMarketWhatsapp ? <WhatsAppMarketing /> : <Navigate to="/" />}
          />
          {renderSettingsRoutes()}
          <Route
            path="/permissions"
            element={canManagePermissions ? <PermissionManager /> : <Navigate to="/" />}
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </Layout>
  );
};

export default RoleBasedRouter;
